import { inject, Injectable } from '@angular/core';
import type { ConnectorRequirement } from './connector.models';
import { ConnectorOAuthService } from './connector-oauth.service';

export const AUTO_RECONNECT_TTL_MS = 5 * 60 * 1000;
const ATTEMPT_KEY_PREFIX = 'connector_auto_connect_at:';

interface AutoReconnectCallbacks {
  onConnected?: (mcpServerId: string) => void | Promise<void>;
  onError?: (mcpServerId: string, error: unknown) => void | Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class ConnectorAutoReconnectService {
  private readonly oauth = inject(ConnectorOAuthService);
  private readonly inFlight = new Set<string>();
  private batchInFlight: Promise<void> | undefined;

  reconnectOkta(
    requirements: readonly ConnectorRequirement[],
    callbacks: AutoReconnectCallbacks = {}
  ): Promise<void> {
    if (this.batchInFlight) return this.batchInFlight;
    const batch = this.runBatch(requirements, callbacks).finally(() => {
      if (this.batchInFlight === batch) this.batchInFlight = undefined;
    });
    this.batchInFlight = batch;
    return batch;
  }

  snooze(mcpServerId: string): void {
    sessionStorage.setItem(attemptKey(mcpServerId), String(Date.now()));
  }

  private async runBatch(
    requirements: readonly ConnectorRequirement[],
    callbacks: AutoReconnectCallbacks
  ): Promise<void> {
    const candidates = requirements.filter(
      (requirement) =>
        requirement.statusResolved &&
        !requirement.connected &&
        requirement.provider?.toLowerCase() === 'okta' &&
        this.shouldAttempt(requirement.mcpServerId)
    );

    for (const requirement of candidates) {
      const mcpServerId = requirement.mcpServerId;
      if (!this.shouldAttempt(mcpServerId)) continue;
      this.inFlight.add(mcpServerId);
      this.snooze(mcpServerId);
      try {
        await this.oauth.connectInPopup(mcpServerId);
        await callbacks.onConnected?.(mcpServerId);
      } catch (error) {
        await callbacks.onError?.(mcpServerId, error);
      } finally {
        this.inFlight.delete(mcpServerId);
      }
    }
  }

  private shouldAttempt(mcpServerId: string): boolean {
    if (this.inFlight.has(mcpServerId)) return false;
    const previous = Number(sessionStorage.getItem(attemptKey(mcpServerId)) ?? 0);
    return Date.now() - previous >= AUTO_RECONNECT_TTL_MS;
  }
}

function attemptKey(mcpServerId: string): string {
  return `${ATTEMPT_KEY_PREFIX}${mcpServerId}`;
}
